import type { RunStatusChip } from '../../helpers/sisSyncRunDisplay'
import type { DataKey } from '../../models/DataObject'
import type { ISisSyncRunFile } from '../../models/SisSyncRun'

// Outlined, like the run's chip, so a file and its run read the same.
const chip = (label: string, color: RunStatusChip['color']): RunStatusChip => ({ label, color, variant: 'outlined' })

// The backend sends RUNNING for the file a live run is importing (its display_status: no finish
// time and the run still holds its claim), so a row's meaning travels with the row. Any other row
// with no finish time stopped partway through: the run moved on or died.
export const getFileStatusChip = (file: Pick<ISisSyncRunFile, 'status' | 'finishedOn'>): RunStatusChip => {
  if (file.status === 'RUNNING') return chip('Running', 'primary')
  if (file.finishedOn == null) return chip('Interrupted', 'error')

  const status: string = file.status
  switch (status) {
    case 'COMPLETED':
      return chip('Completed', 'success')
    case 'COMPLETED_WITH_ERRORS':
      return chip('Completed with errors', 'warning')
    case 'FAILED':
      return chip('Failed', 'error')
    case 'SKIPPED':
      return chip('Skipped', 'default')
    default:
      return chip(status, 'default')
  }
}

// A completed file can still carry warnings; the label keeps them from hiding behind a green chip.
export const getFileWarningsLabel = (file: Pick<ISisSyncRunFile, 'warningCount'>): string | null => {
  if (file.warningCount <= 0) return null
  return `${file.warningCount.toLocaleString()} warning${file.warningCount === 1 ? '' : 's'}`
}

// Warnings show as a chip in the Status cell rather than a column of their own.
export const toFileTableKeys = (keys: DataKey[]): DataKey[] => keys.filter((key) => key.field !== 'warning_count')
